/**
 * SIM-SZENE LEIPZIG – Share (Screenshot + Web Share API)
 * Game-Over-Ergebnis als Bild teilen oder Text kopieren.
 */
import html2canvas from 'html2canvas';

const GAME_TITLE = 'SIM-SZENE LEIPZIG';

/**
 * Erzeugt einen Share-Text aus den Spielstatistiken.
 */
export function getGameOverShareText({ round, phase = 1, currentPathId, pathLabels = {} }) {
  const pathLabel = currentPathId ? (pathLabels[currentPathId] ?? currentPathId) : null;
  let text = `${round} Runden überlebt, Phase ${phase}`;
  if (pathLabel) text += `, Pfad ${pathLabel}`;
  text += ` – ${GAME_TITLE}`;
  return text;
}

/**
 * Captured ein DOM-Element als PNG-Blob (für Share oder Download).
 * @param {HTMLElement} element
 * @returns {Promise<Blob>}
 */
export async function captureElementAsBlob(element) {
  if (!element) throw new Error('Kein Element');
  const canvas = await html2canvas(element, {
    useCORS: true,
    scale: 2,
    backgroundColor: null,
    logging: false,
  });
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas toBlob fehlgeschlagen'))),
      'image/png',
      0.92
    );
  });
}

/**
 * Teilt das Game-Over-Ergebnis: Screenshot + Text.
 * Versucht Web Share API (mit Bild), Fallback: Download des Bildes + Copy-Text.
 * @param {Object} opts
 * @param {HTMLElement} opts.element – z. B. .game-over-content
 * @param {number} opts.round
 * @param {number} opts.phase
 * @param {string|null} opts.currentPathId
 * @param {Record<string,string>} opts.pathLabels
 * @returns {Promise<'shared'|'downloaded'|'copied'|'unsupported'>}
 */
export async function shareGameOverResult({ element, round, phase, currentPathId, pathLabels }) {
  const shareText = getGameOverShareText({ round, phase, currentPathId, pathLabels });
  let blob;
  try {
    blob = await captureElementAsBlob(element);
  } catch (e) {
    console.warn('Screenshot fehlgeschlagen:', e);
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(shareText);
      return 'copied';
    }
    return 'unsupported';
  }

  const file = new File([blob], 'sim-szene-leipzig-ergebnis.png', { type: 'image/png' });
  const shareData = {
    title: GAME_TITLE,
    text: shareText,
    url: window.location.href,
  };

  if (navigator.share && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ ...shareData, files: [file] });
      return 'shared';
    } catch (err) {
      if (err.name === 'AbortError') return 'unsupported';
    }
  }

  if (navigator.share) {
    try {
      await navigator.share(shareData);
      return 'shared';
    } catch (err) {
      if (err.name === 'AbortError') return 'unsupported';
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'sim-szene-leipzig-ergebnis.png';
  a.click();
  URL.revokeObjectURL(url);

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(shareText);
      return 'copied';
    }
  } catch (_) {}
  return 'downloaded';
}

/**
 * Share-Text für ein Szenario (für Social Media / Copy).
 */
export function getScenarioShareText({ title, categoryLabel }) {
  const cat = categoryLabel ? ` [${categoryLabel}]` : '';
  return `„${title}"${cat} – ${GAME_TITLE}`;
}

/**
 * Teilt ein Szenario als Bild + Text (Web Share API oder Fallback).
 * @param {Object} opts
 * @param {HTMLElement} opts.element – z. B. .scenario-panel
 * @param {string} opts.title – Szenario-Titel
 * @param {string} [opts.categoryLabel] – Kategorie-Label
 * @returns {Promise<'shared'|'downloaded'|'copied'|'unsupported'>}
 */
export async function shareScenarioCard({ element, title, categoryLabel }) {
  const shareText = getScenarioShareText({ title, categoryLabel });
  let blob;
  try {
    blob = await captureElementAsBlob(element);
  } catch (e) {
    console.warn('Szenario-Screenshot fehlgeschlagen:', e);
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(shareText);
      return 'copied';
    }
    return 'unsupported';
  }

  const safeTitle = title
    .replace(/[^\w\säöüÄÖÜß-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 50) || 'szenario';
  const file = new File([blob], `sim-szene-leipzig-${safeTitle}.png`, { type: 'image/png' });
  const shareData = {
    title: GAME_TITLE,
    text: shareText,
    url: window.location.href,
  };

  if (navigator.share && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ ...shareData, files: [file] });
      return 'shared';
    } catch (err) {
      if (err.name === 'AbortError') return 'unsupported';
    }
  }

  if (navigator.share) {
    try {
      await navigator.share(shareData);
      return 'shared';
    } catch (err) {
      if (err.name === 'AbortError') return 'unsupported';
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `sim-szene-leipzig-${safeTitle || 'szenario'}.png`;
  a.click();
  URL.revokeObjectURL(url);

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(shareText);
      return 'copied';
    }
  } catch (_) {}
  return 'downloaded';
}
